import { observer } from 'mobx-react-lite';
import * as React from 'react';
import type { AssetCommonViewProps } from 'owa-timestream-asset-sdk-types';
import useTaskListAssetViewState from '../hooks/useTaskListAssetViewState';
import { TaskListView } from 'owa-todo-list';
import type { TaskListPayload } from '../schema/TaskListPayload';
import { ControlIcons } from 'owa-control-icons';
import * as styles from './styles.scss';
import {
    ButtonTextContainerOffset,
    TaskListAssetActionSource,
    TaskListAssetCategoryActionSource,
} from '../constants';
import type { IStyle } from '@fluentui/style-utilities';
import { useAssetPerfDatapoint } from 'owa-timestream-asset-sdk';
import { TodoItemCallout } from 'owa-todo-peek';
import { isFeatureEnabled } from 'owa-feature-flags';
import { lazyMountAndShowFullOptions } from 'owa-options-view';

const commandBarButtonMenuProps = {
    calloutProps: {
        calloutMaxHeight: 300,
        calloutWidth: 220,
    },
};

export default observer(function TaskListAssetView(props: AssetCommonViewProps<TaskListPayload>) {
    const assetPerfDatapoint = useAssetPerfDatapoint();
    const {
        scenarioId,
        onCallback,
        loadPanelDataConfig,
        containerRef,
        taskListPickerMaxWidth,
        calloutTarget,
        onCalloutDismissCallback,
        todoId,
    } = useTaskListAssetViewState(props);
    const folderPickerContainerStyles: React.CSSProperties = {
        maxWidth: taskListPickerMaxWidth,
    };
    const folderPickerTextContainerStyles: IStyle = {
        maxWidth: taskListPickerMaxWidth - ButtonTextContainerOffset,
        minWidth: 0, // Needed to allow children to truncate text; See https://css-tricks.com/flexbox-truncated-text/
    };
    const customStyles = {
        folderPickerContainerStyles: folderPickerContainerStyles,
        folderPickerTextContainerStyles: folderPickerTextContainerStyles,
        folderPickerContainerClass: styles.folderPickerContainer,
        folderPickerLabel: styles.folderPickerLabel,
    };

    const preventAssetDrag = (mouseDownEvent: MouseEvent) => {
        mouseDownEvent.stopPropagation();
    };
    const iconLabelProps = {
        iconName: ControlIcons.ToDoLogoOutline,
        className: styles.folderPickerIcon,
    };

    function onManageTodoItemCalloutCategories() {
        lazyMountAndShowFullOptions.importAndExecute('general', 'categories');
    }
    return (
        <div className={styles.container} ref={containerRef}>
            <TaskListView
                scenarioId={scenarioId}
                todoScenarioConfig={{ source: 'SpacesTaskListAsset' }}
                callback={onCallback}
                loadPanelDataConfig={loadPanelDataConfig}
                viewConfig={{
                    listPicker: {
                        menuProps: commandBarButtonMenuProps,
                        styles: customStyles,
                        iconLabelProps: iconLabelProps,
                        onWheel,
                    },
                    todoListItemDraggable: {
                        dragDropProps: {
                            onMouseDown: preventAssetDrag,
                        },
                    },
                }}
                perfDatapoint={assetPerfDatapoint}
                isInlineEditingDisabled={isFeatureEnabled('cal-board-taskListDetailsCallout')}
            />
            {isFeatureEnabled('cal-board-taskListDetailsCallout') && (
                <TodoItemCallout
                    target={calloutTarget}
                    onCalloutDismiss={onCalloutDismissCallback}
                    isTodoItemCalloutVisible={!!calloutTarget}
                    todoId={todoId}
                    scenarioId={scenarioId}
                    actionSource={TaskListAssetActionSource}
                    categoryActionSource={TaskListAssetCategoryActionSource}
                    onManageCategories={onManageTodoItemCalloutCategories}
                />
            )}
        </div>
    );
});

function onWheel(ev: React.WheelEvent<HTMLDivElement>) {
    // prevent canvas from panning
    ev.stopPropagation();
}
